import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
	MaterialReactTable,
	useMaterialReactTable,
} from 'material-react-table';
import { makeApiRequest, fetchSeatSummary, removeUser } from '../../Shared/Api';
import { PRO_INCLUDED_SEATS } from '../../Shared/pricing';
import {
	Alert,
	Box,
	Button,
	IconButton,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import {
	Add as AddIcon,
	DeleteOutline as DeleteIcon,
	Edit as EditIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import EditUser from '../../Components/Modals/EditUser';
import SeatBillingConfirmModal from '../../Components/Modals/SeatBillingConfirmModal';
import MobileCreateFab from '../../Components/MobileCreateFab';
import { useAppContext, ACTION_TYPES } from '../../Context/AppContext';
import { useFeature } from '../../Hooks/useFeature';
import UsersMobileList from './components/UsersMobileList';

const Users = () => {
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
	const [users, setUsers] = useState([]);
	const [seatSummary, setSeatSummary] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [userModalOpen, setUserModalOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);
	const [removeTarget, setRemoveTarget] = useState(null);
	const [removePreview, setRemovePreview] = useState(null);
	const [removeLoading, setRemoveLoading] = useState(false);
	const { dispatch, state } = useAppContext();
	const { enqueueSnackbar } = useSnackbar();
	const isSuperuser = state.user.isSuperuser;
	const hasTeamAccounts = useFeature('team_accounts');
	const showSeatBanner = hasTeamAccounts && !isSuperuser;

	const fetchUsers = useCallback(async () => {
		setIsLoading(true);
		try {
			const requests = [makeApiRequest('GET', '/auth/users/')];
			if (showSeatBanner) {
				requests.push(fetchSeatSummary());
			}
			const [usersResponse, summaryResponse] = await Promise.all(requests);
			setUsers(usersResponse || []);
			if (summaryResponse) {
				setSeatSummary(summaryResponse);
			}
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to fetch users.', {
				variant: 'error',
			});
		} finally {
			setIsLoading(false);
		}
	}, [enqueueSnackbar, showSeatBanner]);

	useEffect(() => {
		dispatch({
			type: ACTION_TYPES.SET_PAGE_TITLE,
			payload: { pageTitle: 'Users' },
		});
		fetchUsers();
	}, [dispatch, fetchUsers]);

	const handleOpen = (user = null) => {
		setSelectedUser(user);
		setUserModalOpen(true);
	};

	const handleClose = () => {
		setUserModalOpen(false);
		setSelectedUser(null);
	};

	const handleRemoveClick = async (user) => {
		if (isSuperuser) {
			if (
				!window.confirm(
					`Deactivate ${user.email || user.username}? This cannot be undone from the Users page.`,
				)
			) {
				return;
			}
			try {
				await removeUser(user.id, true);
				enqueueSnackbar('User deactivated.', { variant: 'success' });
				fetchUsers();
			} catch (error) {
				enqueueSnackbar(error.message || 'Failed to remove user.', {
					variant: 'error',
				});
			}
			return;
		}

		try {
			const response = await removeUser(user.id, false);
			if (response?.billing_preview) {
				setRemoveTarget(user);
				setRemovePreview(response.billing_preview);
				return;
			}
			enqueueSnackbar('User removed.', { variant: 'success' });
			fetchUsers();
		} catch (error) {
			if (error.status === 402 && error.data?.billing_preview) {
				setRemoveTarget(user);
				setRemovePreview(error.data.billing_preview);
				return;
			}
			enqueueSnackbar(error.message || 'Failed to remove user.', {
				variant: 'error',
			});
		}
	};

	const handleConfirmRemove = async () => {
		if (!removeTarget) {
			return;
		}
		setRemoveLoading(true);
		try {
			await removeUser(removeTarget.id, true);
			enqueueSnackbar('User removed and billing updated.', {
				variant: 'success',
			});
			setRemoveTarget(null);
			setRemovePreview(null);
			fetchUsers();
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to remove user.', {
				variant: 'error',
			});
		} finally {
			setRemoveLoading(false);
		}
	};

	const columns = useMemo(
		() => [
			{
				header: 'Username',
				accessorKey: 'username',
			},
			{
				header: 'First name',
				accessorKey: 'first_name',
			},
			{
				header: 'Last name',
				accessorKey: 'last_name',
			},
			{
				header: 'Email',
				accessorKey: 'email',
			},
			{
				header: 'Role',
				accessorKey: 'role',
			},
			{
				header: 'Hourly cost',
				accessorKey: 'hourly_cost',
				Cell: ({ cell }) => {
					const value = cell.getValue();
					if (value == null || value === '') return '—';
					const n = Number(value);
					if (Number.isNaN(n)) return '—';
					return new Intl.NumberFormat('en-US', {
						style: 'currency',
						currency: 'USD',
					}).format(n);
				},
			},
		],
		[],
	);

	const renderAddUserButton = () => (
		<Button
			variant="contained"
			startIcon={<AddIcon />}
			onClick={() => handleOpen()}
			sx={{
				textTransform: 'none',
				fontWeight: 600,
			}}
		>
			Add user
		</Button>
	);

	const table = useMaterialReactTable({
		columns,
		data: users,
		layoutMode: 'semantic',
		enableRowActions: true,
		positionActionsColumn: 'last',
		state: {
			isLoading,
		},
		renderRowActions: ({ row }) => (
			<Box sx={{ display: 'flex', gap: '0.5rem' }}>
				<Tooltip title="Edit">
					<IconButton onClick={() => handleOpen(row.original)}>
						<EditIcon />
					</IconButton>
				</Tooltip>
				<Tooltip title="Remove">
					<IconButton
						color="error"
						onClick={() => handleRemoveClick(row.original)}
					>
						<DeleteIcon />
					</IconButton>
				</Tooltip>
			</Box>
		),
		renderTopToolbarCustomActions: renderAddUserButton,
		muiTablePaperProps: {
			elevation: 2,
			sx: {
				borderRadius: '8px',
			},
		},
		muiTableHeadCellProps: {
			sx: {
				fontWeight: 'bold',
				fontSize: '0.95rem',
			},
		},
	});

	const seatBanner =
		showSeatBanner && seatSummary ? (
			<Alert severity="info" sx={{ mb: 2 }}>
				<Typography variant="body2" fontWeight={600}>
					{seatSummary.active_seat_count}{' '}
					{seatSummary.active_seat_count === 1 ? 'seat' : 'seats'} used
					{seatSummary.prepaid_unlimited_seats
						? ' (unlimited during prepaid)'
						: ` of ${seatSummary.included_seats}`}
				</Typography>
				{seatSummary.prepaid_unlimited_seats ? (
					<Typography variant="body2" color="text.secondary">
						Unlimited seats during your 3-month prepaid term. Any seats above {seatSummary.included_seats ?? PRO_INCLUDED_SEATS}
						will be billed at ${seatSummary.extra_seat_monthly_usd}/mo once
						monthly billing begins
						{seatSummary.future_billable_extra_seats > 0
							? ` (you currently have ${seatSummary.future_billable_extra_seats} future extra seat${seatSummary.future_billable_extra_seats === 1 ? '' : 's'}).`
							: '.'}
					</Typography>
				) : seatSummary.billable_extra_seats > 0 ? (
					<Typography variant="body2" color="text.secondary">
						{seatSummary.billable_extra_seats} extra seat
						{seatSummary.billable_extra_seats === 1 ? '' : 's'} at $
						{seatSummary.extra_seat_monthly_usd}/mo each (+$
						{seatSummary.monthly_extra_cost_usd}/mo total)
					</Typography>
				) : (
					<Typography variant="body2" color="text.secondary">
						Seats 1–{seatSummary.included_seats ?? PRO_INCLUDED_SEATS} are included in your Pro subscription.
					</Typography>
				)}
			</Alert>
		) : null;

	return (
		<Box sx={{ width: '100%' }}>
			{seatBanner}
			{isDesktop ? (
				<MaterialReactTable table={table} />
			) : (
				<>
					<UsersMobileList
						users={users}
						isLoading={isLoading}
						onEdit={handleOpen}
						onRemove={handleRemoveClick}
					/>
					<MobileCreateFab label="Add user" onClick={() => handleOpen()} />
				</>
			)}
			<EditUser
				open={userModalOpen}
				onClose={handleClose}
				onSubmit={fetchUsers}
				selectedUser={selectedUser}
			/>
			<SeatBillingConfirmModal
				open={Boolean(removeTarget)}
				onClose={() => {
					setRemoveTarget(null);
					setRemovePreview(null);
				}}
				onConfirm={handleConfirmRemove}
				title="Remove user"
				description={
					removeTarget
						? `Remove ${removeTarget.email || removeTarget.username}? They will be deactivated and lose access immediately.`
						: ''
				}
				preview={removePreview}
				loading={removeLoading}
				confirmLabel="Remove user"
			/>
		</Box>
	);
};

export default Users;
